/**
 * Zustand store for AI provider detection and selection.
 * Persists the selected provider across page navigations.
 * Provider detection runs once at AdminLayout mount.
 */
import { create } from 'zustand'
import { detectBrowserAI } from '@/lib/ai/browserProvider'

export interface ProviderInfo {
  id: string
  name: string
  available: boolean
  model?: string
  detail?: string
}

interface AIProviderState {
  providers: ProviderInfo[]
  selectedProvider: string | null
  selectedModel: string | null
  loading: boolean
  error: string | null
  detected: boolean

  detect: () => Promise<void>
  selectProvider: (id: string, model?: string) => void
  reset: () => void
}

export const useAIProviderStore = create<AIProviderState>((set, get) => ({
  providers: [],
  selectedProvider: null,
  selectedModel: null,
  loading: false,
  error: null,
  detected: false,

  detect: async () => {
    // Skip if already detected in this session
    if (get().detected) return

    set({ loading: true, error: null })

    // Run browser-local detection in parallel with the server fetch so a
    // broken /api/admin/ai/providers response can't hide the browser option.
    const browserProviderPromise = detectBrowserAI().catch(() => null)

    let providers: ProviderInfo[] = []
    let recommended: string | null = null
    let recommendedModel: string | null = null
    let detectError: string | null = null

    try {
      const res = await fetch('/api/admin/ai/providers', { credentials: 'include' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      const json = await res.json() as {
        data: {
          providers: ProviderInfo[]
          recommended: string | null
          recommended_model: string | null
        }
      }

      providers = json.data.providers
      recommended = json.data.recommended
      recommendedModel = json.data.recommended_model
    } catch (err) {
      detectError = err instanceof Error ? err.message : 'Detection failed'
    }

    const browserProvider = await browserProviderPromise
    if (browserProvider) {
      providers = [...providers, browserProvider]
      // Fall back to browser AI only if the server recommended nothing
      // — existing Ollama/Claude preference is preserved.
      if (!recommended && browserProvider.available) {
        recommended = browserProvider.id
        recommendedModel = browserProvider.model || null
      }
    }

    set({
      providers,
      selectedProvider: recommended,
      selectedModel: recommendedModel,
      loading: false,
      detected: true,
      error: detectError,
    })
  },

  selectProvider: (id, model) => {
    const provider = get().providers.find((p) => p.id === id)
    set({
      selectedProvider: id,
      selectedModel: model || provider?.model || null,
    })
  },

  reset: () => {
    set({ providers: [], selectedProvider: null, selectedModel: null, loading: false, error: null, detected: false })
  },
}))
